/**
 * Status/severity/adequacy/verdict chips for the Books & Records panel.
 * All built on the platform `<Badge color=… variant="soft">` so they're
 * consistent with badges used across the app.
 */

import { Badge, type BadgeColor } from "@/ui/primitives/badge";
import { STATE_META } from "./meta";
import type { AdequacyStatus, ItemState, Severity, Verdict } from "./types";

export function StateChip({ state }: { state: ItemState }) {
  const s = STATE_META[state];
  const Icon = s.icon;
  return (
    <Badge
      color={s.color}
      variant="soft"
      size="md"
      className="gap-1 whitespace-nowrap"
    >
      <Icon className="size-3" />
      {s.label}
    </Badge>
  );
}

const SEVERITY_COLOR: Record<Severity, BadgeColor> = {
  critical: "red",
  high: "orange",
  medium: "amber",
  low: "gray",
};

export function SeverityChip({ severity }: { severity: Severity }) {
  return (
    <Badge
      color={SEVERITY_COLOR[severity]}
      variant="soft"
      size="md"
      className="capitalize"
    >
      {severity}
    </Badge>
  );
}

const ADEQUACY_COLOR: Record<AdequacyStatus, BadgeColor> = {
  complete: "emerald",
  partial: "amber",
  inadequate: "red",
  unclear: "gray",
};

const ADEQUACY_LABEL: Record<AdequacyStatus, string> = {
  complete: "Complete",
  partial: "Partial",
  inadequate: "Inadequate",
  unclear: "Unclear",
};

export function AdequacyChip({ status }: { status: AdequacyStatus }) {
  return (
    <Badge color={ADEQUACY_COLOR[status]} variant="soft" size="md">
      {ADEQUACY_LABEL[status]}
    </Badge>
  );
}

const VERDICT_META: Record<Verdict, { label: string; color: BadgeColor }> = {
  adequate: { label: "Adequate", color: "emerald" },
  partially_adequate: { label: "Partially Adequate", color: "amber" },
  inadequate: { label: "Inadequate", color: "red" },
  indeterminate: { label: "Indeterminate", color: "gray" },
};

export function VerdictBadge({ verdict }: { verdict: Verdict }) {
  const v = VERDICT_META[verdict];
  return (
    <Badge color={v.color} variant="soft" size="lg">
      {v.label}
    </Badge>
  );
}
